import type { ApprovalCellKind } from './resolveApprovalCellContent';

interface ApprovalCellContentProps {
  kind: ApprovalCellKind;
  /** kind==='signature'일 때만 사용 */
  signatureData?: string;
  name: string;
  /** placeholder일 때 클릭 진입점(작성자 서명 등) */
  onClick?: () => void;
  /** lg=세로 박스(상세 결재란), sm=stepper 미니박스 */
  size?: 'lg' | 'sm';
  /** screen=Tailwind(상호작용), print=.signature-* 정적 마크업(PDF) */
  mode?: 'screen' | 'print';
}

/**
 * 결재 셀의 "내용물"(서명칸 안)만 렌더하는 컴포넌트.
 * 결재란 레이아웃(박스 테두리·stepper dot·라벨·이름행)은 소비처가 유지한다.
 *
 * kind는 resolveApprovalCellContent SSOT가 판정한 표현 종류:
 * - signature   → 서명 이미지
 * - rejected    → "반려" 표시
 * - empty       → 빈칸(null)
 * - placeholder → (screen) onClick 있으면 "클릭하여 서명"(파랑), 없으면 "서명"(회색)
 *                 (print) onClick 있으면 "클릭하여 서명" span, 없으면 빈칸(null)
 *
 * mode:
 * - screen → Tailwind 클래스(상호작용 포함)
 * - print  → .signature-* 정적 마크업(`approval-form.css`) — 양식 결재란(StepperLayout)이 화면·PDF 공통으로 쓴다
 */
export function ApprovalCellContent({
  kind,
  signatureData,
  name,
  onClick,
  size = 'lg',
  mode = 'screen',
}: ApprovalCellContentProps) {
  const isLg = size === 'lg';

  if (mode === 'print') {
    if (kind === 'signature') {
      return <img src={signatureData} alt={`${name} 서명`} className="signature-image" />;
    }
    if (kind === 'rejected') {
      return <div className="signature-rejected">반려</div>;
    }
    // empty → null. placeholder → onClick 있으면 안내 span, 없으면 빈칸.
    if (kind === 'placeholder' && onClick) {
      return (
        <span className="text-brand-500 text-[10px] font-medium whitespace-nowrap">클릭하여 서명</span>
      );
    }
    return null;
  }

  if (kind === 'empty') {
    return null;
  }

  if (kind === 'signature') {
    return (
      <img
        src={signatureData}
        alt={`${name} 서명`}
        className={`object-contain ${
          isLg ? 'max-w-[60px] max-h-[36px]' : 'max-w-[40px] max-h-[24px]'
        }`}
      />
    );
  }

  if (kind === 'rejected') {
    return (
      <span
        className={`text-red-600 font-bold ${isLg ? 'text-[10px]' : 'text-[9px]'}`}
      >
        반려
      </span>
    );
  }

  // placeholder
  if (onClick) {
    return (
      <span className={`text-brand-500 ${isLg ? 'text-[10px]' : 'text-[9px]'}`}>
        클릭하여 서명
      </span>
    );
  }
  return (
    <span className={`text-gray-300 ${isLg ? 'text-[10px]' : 'text-[9px]'}`}>
      서명
    </span>
  );
}
